export const ENDPOINTS = {
  admin: {
    login: '/api/admin/login',
    me: '/api/admin/me',
    resetPassword: '/api/admin/reset-password',
    analyticsOverview: '/api/admin/analytics/overview',
    crmAdminStats: '/api/admin/crm/admin-stats',
    deliveryHealth: '/api/admin/whatsapp/delivery-health',
    tenantsRecent: '/api/admin/tenants/recent',
    tenants: '/api/admin/tenants',
    tenant: (tenantId: string) => `/api/admin/tenants/${tenantId}`,
    extendTrial: (tenantId: string) => `/api/admin/tenants/${tenantId}/extend-trial`,
    modelPolicy: (tenantId: string) => `/api/admin/tenants/${tenantId}/model-policy`,
    whatsappConfig: (tenantId: string) => `/api/admin/tenants/${tenantId}/whatsapp-config`,
    whatsappConfigReset: (tenantId: string) => `/api/admin/tenants/${tenantId}/whatsapp-config/reset`,
    whatsappSessionStatus: (tenantId: string) => `/admin/whatsapp/${tenantId}/session-status`,
    whatsappQrImage: (tenantId: string) => `/admin/whatsapp/${tenantId}/qr-image`,
    whatsappRestartSession: (tenantId: string) =>
      `/admin/whatsapp/${tenantId}/restart-session?confirm_unpair=true`,
    whatsappLogoutSession: (tenantId: string) =>
      `/admin/whatsapp/${tenantId}/logout-session?confirm_unpair=true`,
    widgetCode: (tenantId: string) => `/api/admin/tenants/${tenantId}/widget-code`,
    crawlStatus: (tenantId: string) => `/api/admin/crawl-status/${tenantId}`,
    crawlSummary: '/api/admin/crawl/summary',
    documents: (tenantId: string) => `/api/admin/documents/${tenantId}`,
    documentUpload: (tenantId: string) => `/api/admin/documents/${tenantId}/upload`,
    documentItem: (tenantId: string, docId: string) => `/api/admin/documents/${tenantId}/${docId}`,
    documentDownload: (tenantId: string, docId: string) => `/api/admin/documents/${tenantId}/${docId}/download`,
    ragProfile: (tenantId: string) => `/api/admin/rag-profile/${tenantId}`,
    plans: '/api/admin/plans',
    plan: (planId: string) => `/api/admin/plans/${planId}`,
    activityLog: '/api/admin/activity-log',
    qualityOverview: '/api/admin/quality/overview',
    qualityReviewQueue: '/api/admin/quality/review-queue',
    qualityLiveScores: '/api/admin/quality/live-scores',
    qualityFailurePatterns: '/api/admin/quality/failure-patterns',
    qualityCandidates: '/api/admin/quality/improvement-candidates',
    qualityCandidateApprove: (id: string) => `/api/admin/quality/improvement-candidates/${id}/approve`,
    qualityCandidateReject: (id: string) => `/api/admin/quality/improvement-candidates/${id}/reject`,
    qualityPipelineRun: '/api/admin/quality/pipeline/run',
    qualityPipelineStatus: '/api/admin/quality/pipeline/status',
    jobRuns: '/api/admin/job-runs',
    industryConfigs: '/api/admin/industry-configs',
    industryConfig: (key: string) => `/api/admin/industry-configs/${key}`,
    industryConfigReset: (key: string) => `/api/admin/industry-configs/${key}/reset`,
    industryAudit: '/api/admin/industry-configs/audit',
    platformOverview: '/api/admin/maintenance/platform-overview',
    handoffSummary: '/api/admin/whatsapp/handoff-summary',
    whatsappTestSend: '/api/admin/whatsapp/test-send',
    whatsappSimulate: '/api/admin/whatsapp/simulate',
    crawl: (tenantId: string) => `/api/admin/crawl/${tenantId}`,
    /* Phase 5.1: Voice AI Platform endpoints */
    voiceOverview: '/api/admin/voice/overview',
    voiceLiveCalls: '/api/admin/voice/live-calls',
    voiceTenantUsage: '/api/admin/voice/tenant-usage',
    voiceProviderHealth: '/api/admin/voice/provider-health',
    voiceDashboard: '/api/admin/voice/dashboard',
    /* Tenant User Management */
    tenantUsers: (tenantId: string) => `/api/admin/tenant-users/${tenantId}`,
    tenantUserCreate: (tenantId: string) => `/api/admin/tenant-users/${tenantId}`,
    tenantUserDetail: (userId: string) => `/api/admin/tenant-users/user/${userId}`,
    tenantUserResetPassword: (userId: string) => `/api/admin/tenant-users/user/${userId}/reset-password`,
    tenantUserToggleActive: (userId: string) => `/api/admin/tenant-users/user/${userId}/toggle-active`,
    tenantUserDelete: (userId: string) => `/api/admin/tenant-users/user/${userId}`,
  },
  billing: {
    paynowPending: '/api/billing/paynow/pending',
    paynowVerify: '/api/billing/paynow/verify',
    invoice: (invoiceId: string) => `/api/billing/invoice/${invoiceId}`,
    checkout: (tenantId: string) => `/api/billing/checkout/${tenantId}`,
    stripePortal: (tenantId: string) => `/api/billing/stripe/portal/${tenantId}`,
    stripeCancel: (tenantId: string) => `/api/billing/stripe/cancel/${tenantId}`,
    publicPlans: '/api/public/plans',
  },
  calendar: {
    status: (tenantId: string) => `/api/calendar/status/${tenantId}`,
    oauthUrl: (tenantId: string) => `/api/calendar/oauth/url/${tenantId}`,
    disconnect: (tenantId: string) => `/api/calendar/disconnect/${tenantId}`,
    availability: (tenantId: string) => `/api/calendar/availability/${tenantId}`,
    initializeDefaults: (tenantId: string) => `/api/calendar/initialize-defaults/${tenantId}`,
  },
  tenant: {
    login: '/api/public/login',
    register: '/api/public/register',
    industries: '/api/public/industries',
    switchTenant: '/api/public/switch-tenant',
    me: '/api/admin/tenant-portal/me',
    changePassword: '/api/admin/tenant-portal/change-password',
    emailSettings: '/api/admin/tenant-portal/email-settings',
    legacyLogin: '/api/admin/tenant-users/auth/login',
    apiKeys: (tenantId: string) => `/api/tenants/${tenantId}/api-keys`,
    regenerateApiKey: (tenantId: string) => `/api/tenants/${tenantId}/api-keys`,
    activities: '/api/admin/tenant-portal/activities',
    unansweredQueries: '/api/admin/tenant-portal/unanswered-queries',
    chatLeads: '/api/admin/tenant-portal/chat-leads',
    whatsappLeads: '/api/admin/tenant-portal/whatsapp-leads',
  },
  channels: {
    whatsappConfigure: (tenantId: string) => `/api/whatsapp/configure/${tenantId}`,
    whatsappHealth: (tenantId: string) => `/api/whatsapp/health/${tenantId}`,
    whatsappOnboard: '/api/whatsapp/onboard',
    whatsappRestartSession: (tenantId: string) =>
      `/api/whatsapp/session/${tenantId}/restart?confirm_unpair=true`,
    whatsappLogoutSession: (tenantId: string) =>
      `/api/whatsapp/session/${tenantId}/logout?confirm_unpair=true`,
    voiceStatus: '/api/voice/status',
  },
  simulators: {
    whatsappSimulate: '/api/tenant/crm/whatsapp/simulate',
    whatsappSimulateReset: '/api/tenant/crm/whatsapp/simulate/reset',
    voiceTurn: (tenantId: string) => `/api/voice/turn/${tenantId}`,
    voiceSimulate: (tenantId: string) => `/api/voice/simulate/${tenantId}`,
    voiceRealtimeVoices: '/api/voice/realtime/voices',
    voiceRealtimeWs: (tenantId: string) => `/api/voice/realtime/${tenantId}`,
  },
  inbox: {
    conversations: '/api/admin/tenant-portal/conversations',
    conversationTranscript: (threadType: string, threadId: string) =>
      `/api/admin/tenant-portal/conversations/${threadType}/${threadId}`,
    agentReply: (sessionId: string) => `/api/chat/handoff/${sessionId}/agent-reply`,
    resumeBot: (sessionId: string) => `/api/chat/handoff/${sessionId}/resume-bot`,
    voiceSessions: (tenantId: string) => `/api/voice/sessions/${tenantId}`,
  },
  crm: {
    followUps: '/api/tenant/crm/follow-ups',
    leads: '/api/tenant/crm/leads',
    lead: (leadId: string) => `/api/tenant/crm/leads/${leadId}`,
    appointments: '/api/tenant/crm/appointments',
    contacts: '/api/tenant/crm/contacts',
    contact: (contactId: string) => `/api/tenant/crm/contacts/${contactId}`,
    contactTimeline: (contactId: string) => `/api/tenant/crm/contacts/${contactId}/timeline`,
    templates: '/api/tenant/crm/templates',
    template: (templateId: string) => `/api/tenant/crm/templates/${templateId}`,
    stats: '/api/tenant/crm/stats',
    deals: '/api/tenant/crm/deals',
    whatsappFlow: '/api/tenant/crm/whatsapp-flow',
    whatsappFlowNode: (nodeId: string) =>
      `/api/tenant/crm/whatsapp-flow/nodes/${encodeURIComponent(nodeId)}`,
    whatsappFlowAttachment: (nodeId: string) =>
      `/api/tenant/crm/whatsapp-flow/nodes/${encodeURIComponent(nodeId)}/attachment`,
    whatsappFlowAttachmentItem: (nodeId: string, documentId: string) =>
      `/api/tenant/crm/whatsapp-flow/nodes/${encodeURIComponent(nodeId)}/attachment/${encodeURIComponent(documentId)}`,
  },
  knowledge: {
    documents: '/api/tenant/crm/documents',
    readiness: (tenantId: string) => `/api/tenants/${tenantId}/knowledge-readiness`,
  },
  home: {
    stats: '/api/admin/tenant-portal/stats',
    botHealth: '/api/admin/tenant-portal/bot-health',
    conversations: '/api/admin/tenant-portal/conversations',
    chatLeads: '/api/admin/tenant-portal/chat-leads',
    pendingActions: '/api/tenant/crm/pending-actions',
    trialStatus: (tenantId: string) => `/api/public/trial/status/${tenantId}`,
    calendarStatus: (tenantId: string) => `/api/calendar/status/${tenantId}`,
  },
  widget: {
    token: (tenantId: string) => `/api/chat/token/${tenantId}`,
  },
  scube: {
    templates: '/api/scube/templates',
    adminQrGenerate: '/api/scube/qr/generate',
    adminQrAnalytics: (qrId: string) => `/api/scube/qr/${qrId}/analytics`,
    adminSessionCreate: '/api/scube/session/create',
    adminSessionHealth: (sessionId: string) => `/api/scube/session/${sessionId}/health`,
    adminSessions: '/api/scube/sessions',
    adminOnboardingStart: '/api/scube/onboarding/start',
    adminOnboardingAdvance: '/api/scube/onboarding/advance',
    adminOnboardingStatus: (tenantId: string) => `/api/scube/onboarding/${tenantId}/status`,
    tenantSession: '/api/scube/tenant/session',
    tenantQrGenerate: '/api/scube/tenant/qr/generate',
    tenantSessionCreate: '/api/scube/tenant/session/create',
    tenantOnboardingStatus: '/api/scube/tenant/onboarding/status',
  },
} as const;

export const STORAGE_KEYS = {
  adminToken: 'admin_token',
  adminUsername: 'admin_username',
  tenantToken: 'tenant_token',
  tenantId: 'tenant_id',
  tenantName: 'tenant_name',
  tenantSlug: 'tenant_slug',
} as const;

/** Tenant Google OAuth — same backend entry as legacy login.html */
export const TENANT_GOOGLE_OAUTH_URL = '/api/public/auth/google?intent=login';
export const TENANT_GOOGLE_REGISTER_URL = '/api/public/auth/google?intent=register';
